import { audio } from '../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../ui/TouchTarget.js';
import { resolveStoryForestLayout, STORY_FOREST_LAYOUT_HEIGHT, STORY_FOREST_LAYOUT_WIDTH } from '../components/StoryForestLayout.js';
import { STORY_FOREST_ASSETS } from '../StoryForestAssets.js';
import {
  STORY_FOREST_DISTRICTS,
  completeStoryForestDistrictProgress,
  getStoryForestState,
  saveStoryForestDistrictProgress,
  visitStoryForestDistrict,
  type StoryForestDistrict,
} from '../StoryForestState.js';
import { CHARACTER_GROVE_ASSETS } from '../minigames/character-grove/CharacterGroveAssets.js';
import { ReadingEvidenceState, type ReadingEvidenceSerializedState } from '../minigames/reading-evidence/ReadingEvidenceState.js';
import type { ReadingEvidenceConfig } from '../minigames/reading-evidence/ReadingEvidenceConfig.js';
import {
  INFERENCE_HOLLOW_ASSETS,
  INFERENCE_HOLLOW_ASSET_LIST,
  canEnterInferenceHollow,
  getMissingInferenceHollowAssets,
} from './InferenceHollowAssets.js';
import { INFERENCE_HOLLOW_PASSAGE } from './InferenceHollowConfig.js';
import { canEnterAuthorsCabin } from '../authors-cabin/AuthorsCabinAssets.js';

export const INFERENCE_HOLLOW_SCENE_KEY = 'InferenceHollow';
const FONT_READING = '"Nunito", "Segoe UI", Arial, sans-serif';

interface InferenceHollowProgress extends ReadingEvidenceSerializedState { readonly restored: boolean; }
interface ChoiceView { id: string; image: Phaser.GameObjects.Image; label: Phaser.GameObjects.Text; badge: Phaser.GameObjects.Text; }
interface ArtButton { image: Phaser.GameObjects.Image; label: Phaser.GameObjects.Text; }

export interface ReadingEvidenceRouteDefinition {
  readonly sceneKey: string;
  readonly sceneTitle: string;
  readonly district: StoryForestDistrict;
  readonly previousScene: string;
  readonly previousLabel: string;
  readonly nextDistrict: StoryForestDistrict;
  readonly nextScene: string;
  readonly nextLabel: string;
  readonly nextUnavailableMessage: string;
  readonly claimPhaseLabel: string;
  readonly evidencePhaseLabel: string;
  readonly completionPageLabel: string;
  readonly config: ReadingEvidenceConfig;
  readonly assets: { readonly dormant: { readonly key: string }; readonly restored: { readonly key: string }; readonly choice: { readonly key: string } };
  readonly assetList: readonly { readonly key: string }[];
  readonly getMissing: (textures: Phaser.Textures.TextureManager) => readonly { readonly key: string; readonly path: string }[];
  readonly canEnter: (textures: Phaser.Textures.TextureManager) => boolean;
  readonly canEnterNext?: (textures: Phaser.Textures.TextureManager) => boolean;
}

const INFERENCE_HOLLOW_ROUTE: ReadingEvidenceRouteDefinition = {
  sceneKey: INFERENCE_HOLLOW_SCENE_KEY,
  sceneTitle: 'INFERENCE HOLLOW',
  district: STORY_FOREST_DISTRICTS.inferenceHollow,
  previousScene: 'MainIdeaMeadow',
  previousLabel: 'MAIN IDEA MEADOW',
  nextDistrict: STORY_FOREST_DISTRICTS.authorsCabin,
  nextScene: 'AuthorsCabin',
  nextLabel: "AUTHOR'S CABIN",
  nextUnavailableMessage: "The cabin path is unlocked. Author's Cabin is awaiting its runtime QA gate.",
  claimPhaseLabel: 'MAKE AN INFERENCE',
  evidencePhaseLabel: 'CONNECT TWO CLUES',
  completionPageLabel: 'INFERENCE COMPLETE',
  config: INFERENCE_HOLLOW_PASSAGE,
  assets: {
    dormant: INFERENCE_HOLLOW_ASSETS.dormant,
    restored: INFERENCE_HOLLOW_ASSETS.restored,
    choice: INFERENCE_HOLLOW_ASSETS.clueLantern,
  },
  assetList: INFERENCE_HOLLOW_ASSET_LIST,
  getMissing: getMissingInferenceHollowAssets,
  canEnter: canEnterInferenceHollow,
  canEnterNext: canEnterAuthorsCabin,
};

export class InferenceHollowScene extends Phaser.Scene {
  static readonly requiredImageKeys = INFERENCE_HOLLOW_ASSET_LIST.map(asset => asset.key);
  private state!: ReadingEvidenceState;
  private dormant!: Phaser.GameObjects.Image;
  private restoredBg!: Phaser.GameObjects.Image;
  private panel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private title!: Phaser.GameObjects.Text;
  private storyTitle!: Phaser.GameObjects.Text;
  private passage!: Phaser.GameObjects.Text;
  private page!: Phaser.GameObjects.Text;
  private previous!: Phaser.GameObjects.Text;
  private next!: Phaser.GameObjects.Text;
  private phase!: Phaser.GameObjects.Text;
  private question!: Phaser.GameObjects.Text;
  private feedback!: Phaser.GameObjects.Text;
  private controls!: Phaser.GameObjects.Text;
  private back!: Phaser.GameObjects.Text;
  private home!: Phaser.GameObjects.Text;
  private onward!: Phaser.GameObjects.Text;
  private choices: ChoiceView[] = [];
  private buttons: ArtButton[] = [];
  private isRestored = false;
  private leaving = false;
  private suspended = false;
  private reducedMotion = false;
  private renderScale = 1;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;
  private readonly route: ReadingEvidenceRouteDefinition;

  constructor(route: ReadingEvidenceRouteDefinition = INFERENCE_HOLLOW_ROUTE) {
    super({ key: route.sceneKey });
    this.route = route;
  }

  create() {
    this.choices = [];
    this.buttons = [];
    this.leaving = false;
    this.suspended = false;

    const world = getStoryForestState();
    const missing = this.route.getMissing(this.textures);
    if (!world.unlockedDistricts.includes(this.route.district)
      || !this.route.canEnter(this.textures)) {
      if (missing.length) console.error(`[${this.route.sceneKey}] Required production textures are missing:`, missing.map(a => `${a.key} (${a.path})`));
      this.scene.start(this.route.previousScene);
      return;
    }
    void audio.enterSoundscape('story-forest');
    this.renderScale = this.configureRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.isRestored = world.completedDistricts.includes(this.route.district);
    this.restoreProgress(world.districtProgress[this.route.district]);
    visitStoryForestDistrict(this.route.district);
    SaveManager.setLastScene(this.route.sceneKey);
    SaveManager.save();
    this.build();
    this.input.keyboard?.on('keydown', this.handleKey, this);
    this.registerLifecycle();
    this.refresh();
    this.applyTextResolution();
    this.cameras.main.fadeIn(this.reducedMotion ? 150 : 440, 62, 94, 88);
  }

  private restoreProgress(value: unknown) {
    const saved = value as Partial<InferenceHollowProgress> | undefined;
    if (saved?.version === 1) {
      this.state = new ReadingEvidenceState(this.route.config, saved);
      this.isRestored = saved.restored === true;
    } else this.state = new ReadingEvidenceState(this.route.config);
  }

  private build() {
    this.dormant = this.add.image(640, 360, this.route.assets.dormant.key).setDisplaySize(1280, 720).setDepth(0);
    this.restoredBg = this.add.image(640, 360, this.route.assets.restored.key).setDisplaySize(1280, 720).setDepth(1);
    this.panel = this.add.image(640, 120, STORY_FOREST_ASSETS.storyMissionPanel.key).setDepth(25);
    this.cosmo = this.add.image(100, 650, STORY_FOREST_ASSETS.storyForestCosmo.key).setDepth(105);
    this.title = this.text(this.route.sceneTitle, 29, '#fff2b8', 'bold').setStroke('#173f2b', 7).setDepth(110);
    this.storyTitle = this.text(this.route.config.title, 20, '#4f3322', 'bold').setDepth(40);
    this.passage = this.text('', 16, '#3e3028', 'normal').setWordWrapWidth(760).setLineSpacing(4).setDepth(40);
    this.page = this.text('', 12, '#6e4a2d', 'bold').setDepth(40);
    this.previous = this.text('← PREVIOUS', 13, '#5d3a22', 'bold').setInteractive({ useHandCursor: true }).setDepth(42);
    this.next = this.text('NEXT →', 13, '#5d3a22', 'bold').setInteractive({ useHandCursor: true }).setDepth(42);
    this.phase = this.text('', 14, '#fff2b8', 'bold').setStroke('#173f2b', 5).setDepth(110);
    this.question = this.text('', 16, '#fff8dc', 'bold').setStroke('#173f2b', 5).setWordWrapWidth(780).setDepth(110);
    this.feedback = this.text('', 13, '#fff8dc', 'bold').setStroke('#173f2b', 5).setWordWrapWidth(760).setDepth(110);
    this.controls = this.text('←/→ pages • 1–4 choose • Enter check • H hint • R reset • Esc meadow • M Home', 10, '#fff8dc', 'bold').setStroke('#173f2b', 5).setDepth(110);
    this.back = this.text(`←  ${this.route.previousLabel}`, 14, '#ffffff', 'bold').setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.home = this.text('HOME HUB  (M)', 14, '#ffffff', 'bold').setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.onward = this.text(`${this.route.nextLabel} • NEXT PATH`, 15, '#fff2b8', 'bold').setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    [this.previous, this.next, this.back, this.home, this.onward]
      .forEach(target => ensureMinimumTextTouchTarget(target));
    this.previous.on('pointerup', () => this.changePage(-1));
    this.next.on('pointerup', () => this.changePage(1));
    this.back.on('pointerup', this.returnToMeadow, this);
    this.home.on('pointerup', this.returnHome, this);
    this.onward.on('pointerup', this.tryNext, this);
    this.choices = Array.from({ length: 4 }, (_v, index) => {
      const image = this.add.image(0, 0, this.route.assets.choice.key).setDepth(60).setInteractive({ useHandCursor: true });
      const label = this.text('', 11, '#3e3028', 'bold').setWordWrapWidth(140).setDepth(65);
      const badge = this.text(`${index + 1}`, 13, '#fff8dc', 'bold').setStroke('#173f2b', 4).setDepth(66);
      image.on('pointerup', () => this.choose(index));
      return { id: '', image, label, badge };
    });
    this.buttons = [this.button('CHECK', () => this.check()), this.button('HINT', () => this.hint()), this.button('RESET', () => this.reset())];
  }

  private text(value: string, size: number, color: string, style: 'normal' | 'bold') {
    return this.add.text(0, 0, value, { fontFamily: FONT_READING, fontSize: `${size}px`, fontStyle: style, color, align: 'center', lineSpacing: 3 }).setOrigin(0.5);
  }

  private button(value: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, CHARACTER_GROVE_ASSETS.connectButton.key).setDepth(100).setInteractive({ useHandCursor: true });
    const label = this.text(value, 13, '#fff8dc', 'bold').setStroke('#173f2b', 4).setDepth(101);
    image.on('pointerup', action);
    return { image, label };
  }

  private changePage(delta: number) {
    if (!this.canAct()) return;
    const index = Phaser.Math.Clamp(this.state.snapshot.pageIndex + delta, 0, this.route.config.pages.length - 1);
    if (!this.state.setPage(index)) return;
    this.feedback.setText('Notice which details best support the answer to the question.');
    audio.click();
    this.capture();
  }

  private choose(index: number) {
    if (!this.canAct()) return;
    const snapshot = this.state.snapshot;
    if (!snapshot.allPagesRead || !snapshot.selectedClaimId) {
      const claim = this.route.config.claimChoices[index];
      if (!claim || !this.state.selectClaim(claim.id)) return;
      this.feedback.setText('Answer chosen. Now connect exactly two details that support it.');
    } else {
      const clue = this.route.config.evidenceDetails[index];
      if (!clue || !this.state.toggleEvidence(clue.id)) return;
      this.feedback.setText('Evidence connection revised. Choose exactly two strong details, then check.');
    }
    audio.click();
    this.capture();
  }

  private check() {
    if (this.isRestored) return this.replay();
    if (!this.canAct()) return;
    const result = this.state.check();
    if (result !== 'complete') {
      const message = result === 'not-read' ? 'Read every page before making an inference.'
        : result === 'incomplete' ? 'Choose one inference and exactly two clues.'
          : result === 'claim-mismatch' ? 'That answer does not fit the whole text. Reset and try another choice.'
            : 'Those details do not best support the answer. Revise the evidence connections.';
      this.feedback.setText(message);
      SaveManager.recordAnswer(this.route.config.skillId, false);
      audio.wrong();
      return this.capture();
    }
    SaveManager.recordAnswer(this.route.config.skillId, true);
    this.isRestored = true;
    completeStoryForestDistrictProgress(this.route.district, this.progress(), this.route.config.completionWorldEvent, this.route.nextDistrict);
    this.feedback.setText(this.route.config.successMessage);
    this.magic();
    audio.restoration();
    this.capture();
  }

  private hint() { if (this.canAct()) { this.feedback.setText(this.state.requestHint()); audio.hint(); this.capture(); } }
  private reset() { if (this.canAct() && this.state.resetSelections()) { this.feedback.setText('The inference and clues are cleared. Read pages stay visited.'); audio.click(); this.capture(); } }
  private replay() { this.isRestored = false; this.state = new ReadingEvidenceState(this.route.config); this.feedback.setText('Replay mode: solve the evidence again without changing saved mastery.'); audio.click(); this.capture(); }
  private capture() { saveStoryForestDistrictProgress(this.route.district, this.progress()); this.refresh(); }
  private progress(): InferenceHollowProgress { return { ...this.state.serialize(), restored: this.isRestored }; }

  private refresh() {
    const s = this.state.snapshot;
    this.restoredBg.setAlpha(this.isRestored ? 1 : 0); this.dormant.setAlpha(this.isRestored ? 0 : 1);
    this.storyTitle.setText(this.isRestored ? 'THE EVIDENCE CONNECTS' : this.route.config.title);
    this.passage.setText(this.isRestored ? `The restoration is complete. The open path leads toward ${this.route.nextLabel}.` : this.route.config.pages[s.pageIndex]);
    this.page.setText(this.isRestored ? this.route.completionPageLabel : `PAGE ${s.pageIndex + 1} OF ${s.pageCount}`);
    this.previous.setVisible(!this.isRestored && s.pageIndex > 0); this.next.setVisible(!this.isRestored && s.pageIndex < s.pageCount - 1);
    const claims = s.allPagesRead && !s.selectedClaimId;
    const evidence = s.allPagesRead && !!s.selectedClaimId;
    this.phase.setText(this.isRestored ? `${this.route.sceneTitle} RESTORED` : !s.allPagesRead ? `READ EVERY PAGE • ${s.visitedPageIndexes.length}/${s.pageCount} visited` : claims ? this.route.claimPhaseLabel : `${this.route.evidencePhaseLabel} • ${s.selectedEvidenceIds.length}/2 selected`);
    const options = claims ? this.route.config.claimChoices : evidence ? this.route.config.evidenceDetails : [];
    this.choices.forEach((view, index) => {
      const option = options[index]; view.id = option?.id ?? '';
      const selected = !!option && (claims ? s.selectedClaimId === option.id : s.selectedEvidenceIds.includes(option.id));
      view.image.setVisible(!this.isRestored && !!option).setAlpha(selected ? 1 : 0.86);
      view.label.setVisible(!this.isRestored && !!option).setText(option ? `${option.text}${selected ? '\nSELECTED' : ''}` : '');
      view.badge.setVisible(!this.isRestored && !!option);
    });
    const chosen = this.route.config.claimChoices.find(c => c.id === s.selectedClaimId)?.text;
    this.question.setVisible(!this.isRestored && s.allPagesRead).setText(evidence ? `Your choice: ${chosen}` : this.route.config.question);
    this.buttons.forEach((button, index) => { const visible = !this.isRestored || index === 0; button.image.setVisible(visible); button.label.setVisible(visible); });
    this.buttons[0].label.setText(this.isRestored ? 'REPLAY' : 'CHECK');
    this.controls.setVisible(!this.isRestored); this.onward.setVisible(this.isRestored);
    if (!this.feedback.text) this.feedback.setText('Read every page, then use exactly two details to support the best answer.');
    this.layout();
  }

  private layout() {
    const { safe, compact } = resolveStoryForestLayout(this); const portrait = safe.width < 700;
    const panelW = portrait ? safe.width - 24 : Math.min(compact ? 760 : 850, safe.width - 190);
    this.panel.setPosition(safe.centerX, safe.y + (portrait ? 145 : compact ? 112 : 125)).setDisplaySize(panelW, panelW * (1086 / 1448));
    this.title.setPosition(safe.centerX, safe.y + 27).setFontSize(portrait ? 21 : compact ? 24 : 29);
    this.back.setPosition(safe.x + (portrait ? 77 : 95), safe.y + 23).setFontSize(portrait ? 8 : compact ? 11 : 14);
    this.home.setPosition(safe.right - (portrait ? 55 : 67), safe.y + 23).setFontSize(portrait ? 9 : compact ? 11 : 14);
    this.storyTitle.setPosition(safe.centerX, safe.y + (portrait ? 78 : 60)).setFontSize(portrait ? 16 : 20);
    this.passage.setPosition(safe.centerX, safe.y + (portrait ? 145 : compact ? 120 : 132)).setFontSize(portrait ? 12 : compact ? 14 : 16).setWordWrapWidth(panelW * 0.76);
    this.page.setPosition(safe.centerX, safe.y + (portrait ? 213 : compact ? 180 : 194));
    this.previous.setPosition(safe.centerX - panelW * 0.25, safe.y + (portrait ? 230 : compact ? 198 : 212));
    this.next.setPosition(safe.centerX + panelW * 0.25, safe.y + (portrait ? 230 : compact ? 198 : 212));
    this.phase.setPosition(safe.centerX, safe.y + (portrait ? 266 : compact ? 230 : 248)).setFontSize(portrait ? 12 : 14);
    this.question.setPosition(safe.centerX, safe.y + (portrait ? 298 : compact ? 262 : 282)).setFontSize(portrait ? 11 : compact ? 14 : 16).setWordWrapWidth(Math.min(780, safe.width - 160));
    const columns = portrait ? 2 : 4;
    const cardW = portrait ? Math.min(150, (safe.width - 50) / 2) : compact ? 165 : 185;
    const gapX = portrait ? cardW + 10 : cardW + 14; const gapY = portrait ? 145 : 0;
    const startY = safe.y + (portrait ? 385 : compact ? 375 : 405);
    this.choices.forEach((view, index) => {
      const row = Math.floor(index / columns), column = index % columns;
      const rowCount = Math.min(columns, this.choices.length - row * columns);
      const x = safe.centerX + (column - (rowCount - 1) / 2) * gapX, y = startY + row * gapY;
      view.image.setPosition(x, y).setDisplaySize(cardW, cardW);
      view.label.setPosition(x, y + 5).setFontSize(portrait ? 8 : compact ? 9 : 10).setWordWrapWidth(cardW * 0.56);
      view.badge.setPosition(x - cardW * 0.33, y - cardW * 0.33).setFontSize(portrait ? 9 : 11);
    });
    const buttonY = safe.bottom - 48, buttonGap = portrait ? Math.min(145, (safe.width - 80) / 2) : 165;
    this.buttons.forEach((button, index) => { const x = safe.centerX + (index - 1) * buttonGap; button.image.setPosition(x, buttonY).setDisplaySize(portrait ? 120 : 140, portrait ? 54 : 62); button.label.setPosition(x, buttonY).setFontSize(portrait ? 10 : 13); });
    this.feedback.setPosition(safe.centerX, safe.bottom - 103).setFontSize(portrait ? 9 : compact ? 11 : 13).setWordWrapWidth(Math.min(760, safe.width - 170));
    this.controls.setPosition(safe.centerX, safe.bottom - 13).setFontSize(portrait ? 7 : compact ? 9 : 10);
    this.onward.setPosition(safe.centerX, safe.bottom - 102).setFontSize(portrait ? 12 : 15);
    this.cosmo.setPosition(safe.x + (compact ? 50 : 66), safe.bottom - (compact ? 49 : 59)).setDisplaySize(compact ? 73 : 90, compact ? 97 : 120);
  }

  private magic() {
    const fx = this.add.image(700, 390, STORY_FOREST_ASSETS.storyMagicVfx.key).setDisplaySize(540, 540).setDepth(95).setBlendMode(Phaser.BlendModes.ADD).setAlpha(this.reducedMotion ? 0.68 : 0);
    if (this.reducedMotion) this.time.delayedCall(800, () => fx.destroy()); else this.tweens.add({ targets: fx, alpha: 0.92, scaleX: 1.14, scaleY: 1.14, duration: 560, yoyo: true, hold: 160, onComplete: () => fx.destroy() });
  }

  private handleKey(event: KeyboardEvent) {
    if (this.leaving || this.suspended) return; const key = event.key.toLowerCase();
    if (key === 'escape') return this.returnToMeadow(); if (key === 'm') return this.returnHome();
    if (key === 'arrowleft') return this.changePage(-1); if (key === 'arrowright') return this.changePage(1);
    if (key === 'enter' || key === ' ') return this.check(); const index = ['1', '2', '3', '4'].indexOf(key);
    if (index >= 0) return this.choose(index); if (key === 'h') return this.hint(); if (key === 'r') this.reset();
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({ onSuspend: () => { this.suspended = true; }, onResume: () => { this.suspended = false; } });
    window.addEventListener('resize', this.handleResize); this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }
  private canAct() { return !this.leaving && !this.suspended && !this.isRestored; }
  private configureRenderer() {
    const w = this.scale.parentSize.width || window.innerWidth, h = this.scale.parentSize.height || window.innerHeight;
    const scale = Phaser.Math.Clamp(Math.min(w / STORY_FOREST_LAYOUT_WIDTH, h / STORY_FOREST_LAYOUT_HEIGHT) * Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2), 1, 2);
    this.scale.scaleMode = Phaser.Scale.FIT; this.scale.autoCenter = Phaser.Scale.CENTER_BOTH; this.scale.displaySize.setAspectMode(Phaser.Scale.FIT);
    this.scale.setGameSize(Math.round(STORY_FOREST_LAYOUT_WIDTH * scale), Math.round(STORY_FOREST_LAYOUT_HEIGHT * scale)); this.scale.refresh(); this.cameras.main.setZoom(scale).centerOn(640, 360); return scale;
  }
  private applyTextResolution() { this.children.list.forEach(object => { if (object instanceof Phaser.GameObjects.Text) object.setResolution(this.renderScale); }); }
  private readonly handleResize = () => { if (this.resizeTimer !== undefined) clearTimeout(this.resizeTimer); this.resizeTimer = window.setTimeout(() => { if (!this.scene.isActive()) return; this.renderScale = this.configureRenderer(); this.layout(); this.applyTextResolution(); }, 80); };
  private readonly returnToMeadow = () => { if (this.leaving) return; this.leaving = true; saveStoryForestDistrictProgress(this.route.district, this.progress()); this.scene.start(this.route.previousScene); };
  private readonly returnHome = () => { if (this.leaving) return; this.leaving = true; saveStoryForestDistrictProgress(this.route.district, this.progress()); SaveManager.setLastScene(this.route.sceneKey); SaveManager.save(); this.scene.start('HomeHub'); };
  private readonly tryNext = () => {
    if (!this.isRestored) return;
    if (!this.route.canEnterNext?.(this.textures)) {
      this.feedback.setText(this.route.nextUnavailableMessage);
      return audio.click();
    }
    this.leaving = true;
    saveStoryForestDistrictProgress(this.route.district, this.progress());
    this.scene.start(this.route.nextScene);
  };
  private shutdown() { if (this.state) saveStoryForestDistrictProgress(this.route.district, this.progress()); this.unsubscribeLifecycle?.(); audio.leaveSoundscape({ id: 'story-forest' }); window.removeEventListener('resize', this.handleResize); if (this.resizeTimer !== undefined) clearTimeout(this.resizeTimer); this.input.keyboard?.off('keydown', this.handleKey, this); this.tweens.killAll(); }
}
